import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError, useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2, Edit3, Eye, MousePointer2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PLACEMENTS = [
  { value: "footer", label: "Footer (toutes les pages)" },
  { value: "landing_top", label: "Landing — haut" },
  { value: "dashboard_sidebar", label: "Dashboard — sidebar" },
  { value: "results_side", label: "Résultats — côté" },
];

const empty = {
  name: "", tagline: "", logo_url: "", click_url: "",
  placement: "footer", active: true, priority: 100,
};

export default function AdminAdvertisers() {
  const { user } = useAuth();
  const [ads, setAds] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/advertisers")
      .then(({ data }) => setAds(data.advertisers))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (ad) => {
    setEditing(ad);
    setForm({
      name: ad.name || "", tagline: ad.tagline || "",
      logo_url: ad.logo_url || "", click_url: ad.click_url || "",
      placement: ad.placement, active: ad.active, priority: ad.priority || 100,
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name || !form.click_url) {
      toast.error("Nom et URL cliquable sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/advertisers/${editing.id}`, form);
        toast.success("Annonceur mis à jour");
      } else {
        await api.post("/admin/advertisers", form);
        toast.success("Annonceur créé");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  const toggleActive = async (ad) => {
    try {
      await api.patch(`/admin/advertisers/${ad.id}`, { active: !ad.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async (ad) => {
    if (!window.confirm(`Supprimer "${ad.name}" ?`)) return;
    try {
      await api.delete(`/admin/advertisers/${ad.id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!ads) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const totalImpressions = ads.reduce((a, x) => a + (x.impressions || 0), 0);
  const totalClicks = ads.reduce((a, x) => a + (x.clicks || 0), 0);
  const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : "0.00";

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="admin-advertisers-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="eyebrow">Administration · Annonceurs</div>
          <h1 className="mt-2 font-display text-3xl lg:text-4xl font-bold tracking-tight">Espace annonceurs</h1>
          <p className="mt-2 text-sm text-muted-foreground">Gérez vos partenaires et emplacements publicitaires.</p>
        </div>
        <Button onClick={openNew} className="rounded-full h-11 px-5" data-testid="new-ad-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouvel annonceur
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-6 border-black/10" data-testid="stats-total"><div className="eyebrow">Total annonceurs</div><div className="mt-3 font-display text-4xl font-black">{ads.length}</div></Card>
        <Card className="p-6 border-black/10" data-testid="stats-active"><div className="eyebrow">Actifs</div><div className="mt-3 font-display text-4xl font-black text-emerald-600">{ads.filter(a=>a.active).length}</div></Card>
        <Card className="p-6 border-black/10" data-testid="stats-impressions"><div className="eyebrow">Impressions</div><div className="mt-3 font-display text-4xl font-black">{totalImpressions.toLocaleString("fr-FR")}</div></Card>
        <Card className="p-6 border-black/10" data-testid="stats-ctr"><div className="eyebrow">CTR</div><div className="mt-3 font-display text-4xl font-black">{ctr}<span className="text-lg text-muted-foreground">%</span></div><div className="text-xs text-muted-foreground mt-1">{totalClicks} clics</div></Card>
      </div>

      <Card className="border-black/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-black/5">
            <tr className="text-left">
              <th className="px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Annonceur</th>
              <th className="px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Emplacement</th>
              <th className="px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Impressions</th>
              <th className="px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Clics</th>
              <th className="px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Actif</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {ads.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">Aucun annonceur. Cliquez sur « Nouvel annonceur ».</td></tr>
            ) : ads.map((ad) => (
              <tr key={ad.id} className="border-b border-black/5 last:border-0 hover:bg-muted/20" data-testid={`ad-row-${ad.id}`}>
                <td className="px-6 py-4">
                  <div className="font-medium flex items-center gap-2">
                    {ad.name}
                    <a href={ad.click_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary" title="Ouvrir">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  {ad.tagline && <div className="text-xs text-muted-foreground mt-0.5">{ad.tagline}</div>}
                </td>
                <td className="px-6 py-4"><Badge variant="outline">{PLACEMENTS.find(p=>p.value===ad.placement)?.label || ad.placement}</Badge></td>
                <td className="px-6 py-4 tabular-nums"><span className="inline-flex items-center gap-1"><Eye className="h-3 w-3 text-muted-foreground" />{ad.impressions.toLocaleString("fr-FR")}</span></td>
                <td className="px-6 py-4 tabular-nums"><span className="inline-flex items-center gap-1"><MousePointer2 className="h-3 w-3 text-muted-foreground" />{ad.clicks.toLocaleString("fr-FR")}</span></td>
                <td className="px-6 py-4"><Switch checked={ad.active} onCheckedChange={() => toggleActive(ad)} data-testid={`toggle-${ad.id}`} /></td>
                <td className="px-6 py-4 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(ad)} data-testid={`edit-${ad.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(ad)} className="text-destructive hover:text-destructive" data-testid={`delete-${ad.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier l'annonceur" : "Nouvel annonceur"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ad-name">Nom *</Label>
              <Input id="ad-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" placeholder="Ex: Be Well Canada Spa" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ad-tagline">Slogan / description courte</Label>
              <Textarea id="ad-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} rows={2} data-testid="form-tagline" placeholder="Ex: Spas haut de gamme fabriqués au Canada" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ad-logo">URL du logo (optionnel)</Label>
              <Input id="ad-logo" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} data-testid="form-logo" placeholder="https://.../logo.png" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ad-url">URL cliquable *</Label>
              <Input id="ad-url" value={form.click_url} onChange={(e) => setForm({ ...form, click_url: e.target.value })} data-testid="form-url" placeholder="https://..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Emplacement</Label>
                <Select value={form.placement} onValueChange={(v) => setForm({ ...form, placement: v })}>
                  <SelectTrigger data-testid="form-placement"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {PLACEMENTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ad-prio">Priorité</Label>
                <Input id="ad-prio" type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 100 })} data-testid="form-priority" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Actif</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="form-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Enregistrer" : "Créer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
